import { useEffect, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import jsPDF from "jspdf";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// Import the Poppins fonts. Ensure these paths are correct.
import "../Reports/Poppins-Regular-normal";
import "../Reports/Poppins-Bold-bold";

// --- TYPE DEFINITIONS ---
interface PatientInfo {
  patientName: string;
  mrn: number;
  guardiansName: string;
  phonNumber: string;
  gender: string;
}
interface ProcedureItem {
  description: string;
  rate: number;
  qty: number;
  amount: number;
  discount: number;
  discountMode: "%" | "value";
}
interface PaymentHistoryItem {
  _id?: string;
  paymentMode?: string;
  payment?: number;
  description?: string;
  receivedBy?: string;
  createdAt?: string;
}

interface Invoice {
  _id: string;
  invoiceId: number;
  patientInfo: PatientInfo;
  procedureItems: ProcedureItem[];
  subTotal: number;
  discount: number;
  grandTotal: number;
  payment: number;
  due: number;
  doctorName: string;
  advance: number;
  payment_mode: string;
  createdBy: string;
  createdAt: string;
  invoiceNote: string;
  paymentHistory?: PaymentHistoryItem[];
}
interface InvoiceThermalReceiptProps {
  invoice: Invoice;
  disabled?: boolean;
  hospitalName?: string;
  hospitalPhone?: string;
  open?: boolean;
}

const InvoiceThermalReceipt: React.FC<InvoiceThermalReceiptProps> = ({
  invoice,
  disabled,
  hospitalName = "Your Hospital Name",
  hospitalPhone = "000-0000000",
  open = false,
}) => {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [pdfContent, setPdfContent] = useState<string>("");
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const printedByName =
    profile?.data?.fullName || profile?.data?.firstName || "";

  const MARGINS = { left: 3, right: 77, contentWidth: 74 };
  const COLUMNS = { item: MARGINS.left, qty: 45, rate: 55, total: 77 };
  const navigate = useNavigate();

  // --- HELPER FUNCTIONS ---
  const safeString = (text: any, placeholder = ""): string =>
    String(text ?? placeholder);
  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  const centerText = (doc: jsPDF, text: string, y: number) => {
    const textWidth =
      (doc.getStringUnitWidth(text) * doc.getFontSize()) /
      doc.internal.scaleFactor;
    doc.text(text, (doc.internal.pageSize.width - textWidth) / 2, y);
  };
  const rightAlignText = (doc: jsPDF, text: string, y: number) => {
    const textWidth =
      (doc.getStringUnitWidth(text) * doc.getFontSize()) /
      doc.internal.scaleFactor;
    doc.text(text, MARGINS.right - textWidth, y);
  };
  const formatCurrency = (num: number | null | undefined) =>
    (num ?? 0).toFixed(2);

  const isPartiallyOrFullyPaid = (invoiceData: Invoice): boolean =>
    (invoiceData.payment ?? 0) > 0;

  const getLastPaymentDate = (invoiceData: Invoice): string => {
    const history = invoiceData.paymentHistory;
    if (!Array.isArray(history) || history.length === 0) {
      return "";
    }

    const lastPayment = [...history].sort(
      (a, b) =>
        new Date(b.createdAt || 0).getTime() -
        new Date(a.createdAt || 0).getTime()
    )[0];

    return lastPayment?.createdAt ? formatDate(lastPayment.createdAt) : "";
  };

  const calculatePdfHeight = (invoiceData: Invoice): number => {
    // Use a temporary jsPDF instance to measure text accurately
    const tempDoc = new jsPDF({ unit: "mm", format: [80, 297] });
    // Define constants for font sizes used in rendering
    const FONT_SIZE_NORMAL = 8;
    const FONT_SIZE_LARGE = 9;

    // This function simulates the `currentY` variable from your `generatePDF` function.
    // Each line here should correspond EXACTLY to a `currentY += ...` line in the renderer.

    let height = 7; // Initial top margin (matches `currentY = 7`)

    // --- Header Section ---
    height += 4.5; // Hospital Name
    height += 4.5; // Phone Number
    height += 4; // Line and space after header

    // --- Invoice Info Section ---
    height += 4.5; // Invoice #
    height += 4.5; // Date
    if (isPartiallyOrFullyPaid(invoiceData)) {
      height += 4.5; // Payment Date
    }
    height += 5; // Generated By
    if (invoiceData.doctorName) {
      height += 4.5; // Doctor
    }

    // --- Patient Info Section ---
    height += 2; // Space before top line
    height += 4; // Top line itself
    height += 4.5; // PT Name / Guardian line
    height += 4.5; // MRN / Phone No line
    // The bottom line is drawn at the current height, so we only add space after it.

    // --- Table Header ---
    height += 3; // Space before table header text
    height += 2; // Space after table header text
    height += 4; // Space after table header line

    // --- Procedure Items (The Dynamic Part) ---
    if (Array.isArray(invoiceData.procedureItems)) {
      invoiceData.procedureItems.forEach(item => {
        if (item) {
          tempDoc.setFont("Poppins-Regular", "normal");
          tempDoc.setFontSize(FONT_SIZE_NORMAL);
          const descriptionLines = tempDoc.splitTextToSize(
            safeString(item.description, "N/A"),
            COLUMNS.qty - COLUMNS.item - 2 // This width must be exact
          );
          // Add 4mm for EACH line the description will take up.
          // This was the main source of your error (`* 4 + 1` was wrong).
          height += descriptionLines.length * 4;
        }
      });
    }

    // --- Totals Section ---
    height += 5; // Space after table items line (changed from 4 to 5)
    height += 5; // Sub Total
    height += 5; // Discount
    height += 5; // Grand Total
    height += 5; // Paid Amount

    // Conditional Fields (Advance and Due)
    if (invoiceData.advance > 0) {
      height += 5; // Advance
    }
    if (invoiceData.due > 0) {
      height += 5; // Due
    }

    height += 3; // Space after totals section (before stamp/footer)

    // --- Invoice Note Section ---
    if (invoiceData.invoiceNote && invoiceData.invoiceNote.trim()) {
      tempDoc.setFont("Poppins-Regular", "normal");
      tempDoc.setFontSize(FONT_SIZE_NORMAL);
      height += 5; // "Invoice Note:" label
      const noteLines = tempDoc.splitTextToSize(
        invoiceData.invoiceNote,
        MARGINS.right - MARGINS.left
      );
      height += noteLines.length * 4; // 4mm per wrapped line
    }

    // --- Footer Section ---
    // The original function had `height += 0`, which was incorrect.
    // The footer text takes up space.
    height += 4; // Payment Mode line
    height += 4; // Printed By
    height += 4; // Printed At

    // --- Final Padding ---
    // Add a small buffer at the very end so the last line isn't cut off.
    height += 8;

    return Math.ceil(height); // Return the final, more accurate height
  };

  const drawDottedLineRow = (
    doc: jsPDF,
    y: number,
    label: string,
    value: string
  ) => {
    doc.setFontSize(9);

    // 1. Draw the Label (normal font)
    doc.setFont("Poppins-Regular", "normal");
    doc.text(label, MARGINS.left, y);
    const labelWidth = doc.getTextWidth(label);

    // 2. Draw the Value (bold font, right-aligned)
    doc.setFont("Poppins-Bold", "bold");
    rightAlignText(doc, value, y); // Using your existing rightAlignText helper
    const valueWidth = doc.getTextWidth(value);

    // 3. Draw the Dotted Line in between
    const startX = MARGINS.left + labelWidth + 1; // Start after label + 1mm padding
    const endX = MARGINS.right - valueWidth - 1; // End before value - 1mm padding

    // Only draw the line if there is space
    if (startX < endX) {
      doc.setLineDashPattern([0.5, 0.5], 0); // Set line style to dotted
      // The y-coordinate is adjusted slightly to align nicely with the text baseline
      doc.line(startX, y - 1, endX, y - 1);
      doc.setLineDashPattern([], 0); // Reset line style to solid for other lines
    }

    // Return the y-position for the next line
    return y + 5; // Using 5mm spacing for a clean look
  };

  const generatePDF = async (forPrint: boolean) => {
    try {
      const calculatedHeight = calculatePdfHeight(invoice); // Make sure to adjust this function too!
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [80, calculatedHeight],
      });

      // --- IMAGE LOADING (No changes here) ---
      let stampImageBase64: string | null = null;
      if (invoice.due === 0 && invoice.grandTotal > 0) {
        try {
          const response = await fetch("/paidStamp.png");
          if (!response.ok) throw new Error("Stamp image not found");

          const blob = await response.blob();
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          stampImageBase64 = dataUrl.split(",")[1];
        } catch (e) {
          console.error("Paid stamp image failed to load:", e);
        }
      }

      let currentY = 7;

      // --- PDF RENDERING ---

      // --- Hospital Header (No changes here) ---
      doc.setFont("Poppins-Bold", "bold");
      doc.setFontSize(11);
      centerText(doc, safeString(hospitalName), currentY);
      currentY += 4.5;

      doc.setFont("Poppins-Regular", "normal");
      doc.setFontSize(8);
      centerText(doc, `Phone: ${safeString(hospitalPhone)}`, currentY);
      currentY += 4.5;
      doc.setDrawColor(0);
      doc.setLineWidth(0.2);
      doc.line(MARGINS.left, currentY, MARGINS.right, currentY);
      currentY += 4;

      // =================== NEW PATIENT & INVOICE INFO SECTION ===================
      // This block replaces the old patient info section completely.

      currentY = drawDottedLineRow(
        doc,
        currentY,
        "P.t name:",
        safeString(invoice.patientInfo?.patientName)
      );
      currentY = drawDottedLineRow(
        doc,
        currentY,
        "Gender:",
        safeString(invoice.patientInfo?.gender)
      );
      currentY = drawDottedLineRow(
        doc,
        currentY,
        "MRN:",
        safeString(invoice.patientInfo?.mrn)
      );
      currentY = drawDottedLineRow(
        doc,
        currentY,
        "Ph no:",
        safeString(invoice.patientInfo?.phonNumber)
      );
      currentY = drawDottedLineRow(
        doc,
        currentY,
        "Invoice #:",
        safeString(invoice.invoiceId)
      );
      currentY = drawDottedLineRow(
        doc,
        currentY,
        "Date:",
        formatDate(invoice.createdAt)
      );
      if (isPartiallyOrFullyPaid(invoice)) {
        currentY = drawDottedLineRow(
          doc,
          currentY,
          "Payment Date:",
          getLastPaymentDate(invoice)
        );
      }
      currentY = drawDottedLineRow(
        doc,
        currentY,
        "Generated By:",
        safeString(invoice.createdBy, "N/A")
      );
      {
        invoice.doctorName &&
          (currentY = drawDottedLineRow(
            doc,
            currentY,
            "Doctor Name:",
            safeString(invoice.doctorName)
          ));
      }
      
      // Invoice Note with proper text wrapping
      if (invoice.invoiceNote && invoice.invoiceNote.trim()) {
        doc.setFont("Poppins-Regular", "normal");
        doc.setFontSize(9);
        doc.text("Invoice Note:", MARGINS.left, currentY);
        currentY += 5;
        
        doc.setFontSize(8);
        const noteLines = doc.splitTextToSize(
          invoice.invoiceNote,
          MARGINS.right - MARGINS.left
        );
        noteLines.forEach((line: string) => {
          doc.text(line, MARGINS.left, currentY);
          currentY += 4;
        });
      }

      currentY += 2; // Add a little space before the next line
      doc.line(MARGINS.left, currentY, MARGINS.right, currentY);
      // =================== END NEW SECTION ===================

      currentY += 3;

      // --- Table Header and Items (No changes here) ---
      doc.setFont("Poppins-Bold", "bold");
      doc.setFontSize(8);
      doc.text("Item Description", COLUMNS.item, currentY);
      doc.text("Qty", COLUMNS.qty, currentY);
      doc.text("Rate", COLUMNS.rate, currentY);
      rightAlignText(doc, "Total", currentY);
      currentY += 2;
      doc.line(MARGINS.left, currentY, MARGINS.right, currentY);
      currentY += 4;

      if (Array.isArray(invoice.procedureItems)) {
        invoice.procedureItems.forEach(item => {
          if (item) {
            doc.setFont("Poppins-Regular", "normal");
            doc.setFontSize(8);
            const descriptionLines = doc.splitTextToSize(
              safeString(item.description, "N/A"),
              COLUMNS.qty - COLUMNS.item - 2
            );
            descriptionLines.forEach((line: any, index: any) => {
              doc.text(line, COLUMNS.item, currentY);
              if (index === 0) {
                doc.text(safeString(item.qty), COLUMNS.qty + 2, currentY, {
                  align: "center",
                });
                doc.text(safeString(item.rate), COLUMNS.rate, currentY);
                rightAlignText(doc, formatCurrency(item.amount), currentY);
              }
              currentY += 4;
            });
          }
        });
      }

      doc.setLineWidth(0.4);
      doc.line(MARGINS.left, currentY, MARGINS.right, currentY);
      currentY += 5;
      doc.setLineWidth(0.2);

      // --- Totals and Stamp Section (No changes here) ---
      const totalsStartY = currentY;

      doc.setFont("Poppins-Regular", "normal");
      doc.setFontSize(9);
      doc.text("Sub Total:", MARGINS.left, currentY);
      rightAlignText(doc, formatCurrency(invoice.subTotal), currentY);
      currentY += 5;

      doc.text("Discount:", MARGINS.left, currentY);
      rightAlignText(doc, formatCurrency(invoice.discount), currentY);
      currentY += 5;

      doc.setFont("Poppins-Bold", "bold");
      doc.text("Grand Total:", MARGINS.left, currentY);
      rightAlignText(doc, formatCurrency(invoice.grandTotal), currentY);
      currentY += 5;

      doc.text("Paid Amount:", MARGINS.left, currentY);
      rightAlignText(doc, formatCurrency(invoice.payment), currentY);
      currentY += 5;

      doc.setFont("Poppins-Regular", "normal");
      if (invoice.advance > 0) {
        doc.text("Advance:", MARGINS.left, currentY);
        rightAlignText(doc, formatCurrency(invoice.advance), currentY);
        currentY += 5;
      }
      if (invoice.due > 0) {
        doc.text("Due:", MARGINS.left, currentY);
        rightAlignText(doc, formatCurrency(invoice.due), currentY);
        currentY += 5;
      }
      const totalsEndY = currentY;

      if (stampImageBase64) {
        const stampWidth = 35;
        const stampHeight = 22;
        const stampX = (doc.internal.pageSize.width - stampWidth) / 2 + 5;
        const stampY =
          totalsStartY + (totalsEndY - totalsStartY - stampHeight) / 2;
        doc.addImage(
          stampImageBase64,
          "PNG",
          stampX,
          stampY,
          stampWidth,
          stampHeight,
          undefined,
          "FAST",
          0
        );
      }
      currentY += 3;

      // --- Footer (No changes here) ---
      doc.setFont("Poppins-Regular", "normal");
      doc.setFontSize(8);
      centerText(
        doc,
        `Payment Mode: ${safeString(invoice.payment_mode)}`,
        currentY
      );
      currentY += 4;
      centerText(
        doc,
        `Printed By: ${safeString(printedByName, "N/A")}`,
        currentY
      );
      currentY += 4;
      centerText(doc, `Printed At: ${formatDate(new Date().toISOString())}`, currentY);

      // --- PDF Output (No changes here) ---
      const pdfDataUri = doc.output("datauristring");
      if (!pdfDataUri || pdfDataUri.length < 100) {
        throw new Error("jsPDF generated an empty document.");
      }

      if (forPrint) {
        // This combination is often blocked by popup blockers.
        // It's better to open the blob in a new window.
        const blob = doc.output("blob");
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
      } else {
        const blob = doc.output("blob");
        const content = URL.createObjectURL(blob);
        setPdfBlob(blob); // Save blob for download/print
        setPdfContent(content);
        setPreviewModalOpen(true);
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "An error occurred while generating the PDF."
      );
      console.error("PDF Generation Error:", error);
    }
  };

  useEffect(() => {
    if (open && invoice) {
      generatePDF(false);
    }
  }, [open, invoice]);

  return (
    <>
      {!disabled && (
        <Modal
          open={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <h2>Invoice Receipt Preview</h2>
            {pdfContent && (
              <iframe
                src={pdfContent}
                title="Invoice Preview"
                width="320px"
                height="500px"
                style={{ border: "1px solid #ccc" }}
              />
            )}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Button
                onClick={() => {
                  if (pdfBlob) {
                    const blobUrl = URL.createObjectURL(pdfBlob);
                    const printWindow = window.open(blobUrl, "_blank");
                    if (printWindow) {
                      printWindow.onload = function () {
                        printWindow.print();
                      };
                    } else {
                      toast.error("Please disable your popup blocker to print.");
                    }
                  }
                }}
                variant="contained"
              >
                Print
              </Button>
              <Button
                onClick={() => {
                  setPreviewModalOpen(false);
                  navigate("/account/invoice");
                }}
                variant="outlined"
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default InvoiceThermalReceipt;
